import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import {
  Target,
  BarChart3,
  Search,
  Lightbulb,
  Shield,
  CheckCircle2,
  Clock,
  Send,
  AlertTriangle,
  ChevronRight,
  Loader2,
  ClipboardList,
  Flag,
  PackageCheck,
  Archive,
  RefreshCw,
} from "lucide-react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  { id: "define", label: "Define", icon: Target, color: "from-blue-500/20 to-blue-600/5", border: "border-blue-500/30", text: "text-blue-400", dot: "bg-blue-400" },
  { id: "measure", label: "Measure", icon: BarChart3, color: "from-amber-500/20 to-amber-600/5", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-400" },
  { id: "analyze", label: "Analyze", icon: Search, color: "from-cyan-500/20 to-cyan-600/5", border: "border-cyan-500/30", text: "text-cyan-400", dot: "bg-cyan-400" },
  { id: "improve", label: "Improve", icon: Lightbulb, color: "from-emerald-500/20 to-emerald-600/5", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400" },
  { id: "control", label: "Control", icon: Shield, color: "from-purple-500/20 to-purple-600/5", border: "border-purple-500/30", text: "text-purple-400", dot: "bg-purple-400" },
  { id: "closure", label: "Closure", icon: PackageCheck, color: "from-orange-500/20 to-orange-600/5", border: "border-orange-500/30", text: "text-orange-400", dot: "bg-orange-400" },
  { id: "closed", label: "Closed", icon: Archive, color: "from-slate-500/15 to-slate-600/5", border: "border-slate-500/20", text: "text-slate-400", dot: "bg-slate-400" },
];

const GATE_STATUS = {
  in_progress: { label: "In Progress", icon: Clock, cls: "text-amber-400 bg-amber-400/10" },
  ready_for_review: { label: "Ready", icon: Send, cls: "text-blue-400 bg-blue-400/10" },
  pending_approval: { label: "Pending", icon: Clock, cls: "text-purple-400 bg-purple-400/10" },
  approved: { label: "Approved", icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-400/10" },
  rejected: { label: "Rejected", icon: AlertTriangle, cls: "text-red-400 bg-red-400/10" },
  closing: { label: "Closing", icon: PackageCheck, cls: "text-orange-400 bg-orange-400/10" },
  completed: { label: "Completed", icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-400/15 ring-1 ring-emerald-400/20" },
};

function GateBadge({ status }) {
  const config = GATE_STATUS[status] || GATE_STATUS.in_progress;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${config.cls}`} data-testid={`gate-badge-${status}`}>
      <Icon className="w-2.5 h-2.5" />
      {config.label}
    </span>
  );
}

function ProjectCard({ project, isClosed }) {
  return (
    <Link
      to={`/project/${project.project_id}`}
      className={`block p-3 border rounded-lg transition-all duration-150 group ${
        isClosed
          ? "bg-slate-900/30 border-slate-700/40 hover:border-slate-500/50 opacity-80 hover:opacity-100"
          : "bg-background/60 border-border/50 hover:border-primary/40 hover:bg-background/80"
      }`}
      data-testid={`pipeline-card-${project.project_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className={`text-sm font-medium leading-tight truncate flex-1 ${isClosed ? "text-slate-400" : ""}`}>
          {project.title}
        </h4>
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <GateBadge status={project.gate_status} />

        {project.total_tasks > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <ClipboardList className="w-2.5 h-2.5" />
            {project.completed_tasks}/{project.total_tasks}
          </span>
        )}

        {project.industry && (
          <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">{project.industry}</span>
        )}
      </div>

      {/* Phase progress dots */}
      <div className="flex items-center gap-1 mt-2">
        {PHASES.slice(0, 6).map((phase) => {
          const isCompleted = project.completed_phases?.includes(phase.id);
          const isCurrent = project.current_phase === phase.id;
          return (
            <div
              key={phase.id}
              className={`w-1.5 h-1.5 rounded-full ${
                isCompleted ? "bg-emerald-400" : isCurrent ? phase.dot : "bg-muted-foreground/20"
              }`}
              title={`${phase.label}${isCompleted ? " (done)" : isCurrent ? " (current)" : ""}`}
            />
          );
        })}
      </div>
    </Link>
  );
}

export default function DMAICPipeline() {
  const { token } = useAuth();
  const location = useLocation();
  const [pipeline, setPipeline] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoPoll, setAutoPoll] = useState(true);

  const fetchPipeline = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/dashboard/pipeline`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPipeline(data.pipeline);
        setSummary(data.summary);
      }
    } catch (e) {
      console.error("Pipeline fetch failed:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  // Refetch on every navigation to this page
  useEffect(() => {
    setLoading(true);
    fetchPipeline();
  }, [token, location.key, fetchPipeline]);

  // Auto-poll every 30s
  useEffect(() => {
    if (!autoPoll) return;
    const interval = setInterval(() => fetchPipeline(), 30000);
    return () => clearInterval(interval);
  }, [autoPoll, fetchPipeline]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading pipeline...
      </div>
    );
  }

  if (!pipeline || summary?.total === 0) return null;

  return (
    <div className="space-y-4" data-testid="dmaic-pipeline">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="font-heading text-lg font-semibold">DMAIC Pipeline</h2>
          <span className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
            {summary?.total} project{summary?.total !== 1 ? "s" : ""}
          </span>
          {summary?.pending_approvals > 0 && (
            <span className="text-xs text-purple-400 bg-purple-400/10 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="pending-approvals-badge">
              <Flag className="w-3 h-3" />
              {summary.pending_approvals} pending approval{summary.pending_approvals !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {autoPoll && (
            <span className="flex items-center gap-1.5 text-[10px] text-emerald-400/80 font-medium" data-testid="live-indicator">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              Live
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoPoll((p) => !p)}
            className={`gap-1 text-[10px] px-2 h-6 ${autoPoll ? "text-emerald-400/70 hover:text-emerald-300" : "text-muted-foreground/50 hover:text-muted-foreground"}`}
            data-testid="auto-poll-toggle"
          >
            {autoPoll ? "Auto" : "Paused"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchPipeline(true)}
            disabled={refreshing}
            className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
            data-testid="pipeline-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Pipeline Columns — 7 columns */}
      <div className="grid grid-cols-7 gap-2.5 min-h-[200px]" data-testid="pipeline-columns">
        {PHASES.map((phase) => {
          const Icon = phase.icon;
          const items = pipeline?.[phase.id] || [];
          const count = items.length;
          const isClosed = phase.id === "closed";

          return (
            <div
              key={phase.id}
              className={`bg-gradient-to-b ${phase.color} border ${phase.border} rounded-lg overflow-hidden ${
                isClosed ? "ring-1 ring-slate-700/30" : ""
              }`}
              data-testid={`pipeline-col-${phase.id}`}
            >
              {/* Column Header */}
              <div className={`px-2.5 py-2 border-b ${isClosed ? "border-slate-700/30" : "border-border/30"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${phase.text}`} />
                    <span className={`text-xs font-semibold ${phase.text}`}>{phase.label}</span>
                  </div>
                  {count > 0 && (
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${phase.text} bg-black/20`}>
                      {count}
                    </span>
                  )}
                </div>
              </div>

              {/* Cards */}
              <div className="p-1.5 space-y-1.5 max-h-[400px] overflow-y-auto">
                {items.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground/40">
                    <div className="w-6 h-6 rounded-full border border-dashed border-muted-foreground/20 mx-auto mb-2" />
                    <p className="text-[10px]">No projects</p>
                  </div>
                ) : (
                  items.map((proj) => <ProjectCard key={proj.project_id} project={proj} isClosed={isClosed} />)
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
